import { debounce, set } from 'lodash/fp'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { graphql } from '~/gql'
import { gqlClient } from '~/hooks/useGraphQL.server'
import { InlineTooltip } from '~/components/Tooltip'
import { useEvaluationTasksInProgress } from '~/app/evaluations/tasksContext'
import { cn } from '~/utils/cn'
import {
  DashboardEvaluationsQuery,
  EvaluationVersionClientQuery,
} from '~/gql/graphql'

const updateEvaluationNameMutation = graphql(`
  mutation updateEvaluationNameMutation(
    $evaluationId: String!
    $evaluationVersion: Int!
    $name: String!
  ) {
    updateEvaluationName(
      evaluationId: $evaluationId
      evaluationVersion: $evaluationVersion
      name: $name
    )
  }
`)

export const useUpdateEvaluationNameMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      evaluationId,
      evaluationVersion,
      name,
    }: {
      evaluationId: string
      evaluationVersion: number
      name: string
    }) =>
      gqlClient().request(updateEvaluationNameMutation, {
        evaluationId,
        evaluationVersion,
        name,
      }),
    onSuccess: (responseData, requestData) => {
      // Update name for the EvaluationVersion query data
      queryClient.setQueryData<EvaluationVersionClientQuery>(
        ['EvaluationVersionClient', { id: requestData.evaluationId }],
        (currentData: EvaluationVersionClientQuery) => {
          if (!currentData) return
          return set(`evaluation.name`, requestData.name, currentData)
        }
      )
      // Also do the same as above for dashboard evaluations
      // Update dashboard evaluations in the cache
      queryClient.setQueryData<DashboardEvaluationsQuery>(
        ['DashboardEvaluations', undefined],
        (currentData: DashboardEvaluationsQuery) => {
          const evaluationIndex =
            currentData?.dashboard?.evaluations?.findIndex(
              (evaluation) => evaluation.id === requestData.evaluationId
            ) ?? -1
          if (evaluationIndex === -1) {
            return currentData
          }
          return set(
            `dashboard.evaluations[${evaluationIndex}].name`,
            requestData.name,
            currentData
          )
        }
      )
    },
  })
}
export function EvaluationName({
  disabled,
  evaluationId,
  evaluationName,
  evaluationVersion,
}: {
  disabled: boolean
  evaluationId: string
  evaluationName: string
  evaluationVersion: number
}) {
  const [name, setName] = useState<string>(evaluationName)
  const tasks = useEvaluationTasksInProgress()
  const updateNameMutation = useUpdateEvaluationNameMutation()
  const inputRef = useRef<HTMLInputElement>(null)

  // Update name if it changes externally e.g via edit modal
  useEffect(() => {
    setName(evaluationName)
  }, [evaluationName])

  const updateName = useCallback(
    debounce(2000, async (evaluationId, evaluationVersion, name) => {
      if (!name?.length) return
      tasks.increase()
      await updateNameMutation.mutateAsync({
        name,
        evaluationId,
        evaluationVersion,
      })
      tasks.decrease()
    }),
    []
  )

  const finishEditing = useCallback(() => {
    if (!name?.length) {
      setName(evaluationName)
    }
  }, [name])

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Escape') {
      event.preventDefault()
      const element = event.target as HTMLInputElement
      element.blur()
      finishEditing()
    }
  }

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value
      setName(name)
      updateName(evaluationId, evaluationVersion, name)
    },
    [evaluationId, evaluationVersion, evaluationName]
  )

  return (
    <div className="">
      <InlineTooltip tooltip="Rename evaluation">
        <label className="input-sizer" data-value={name}>
          <input
            type="text"
            value={name}
            ref={inputRef}
            disabled={disabled}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            placeholder="Evaluation Name"
            className={cn(
              'w-min-[12ch] truncate px-0.5 py-1 rounded font-medium tracking-normal leading-none outline-none border-1 border-transparent bg-transparent focus:ring-0 focus:border-blue-600 transition-colors duration-200 ease-in-out',
              disabled
                ? 'cursor-default'
                : 'cursor-pointer hover:border-gray-300'
            )}
          />
        </label>
      </InlineTooltip>
    </div>
  )
}
